import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FaSpinner } from "react-icons/fa";
import { LuLock } from "react-icons/lu";
import { toast } from "react-toastify";
import { useForgetPasswordMutation } from "../../../Redux/Api/Signup";
import useCiT from "../../../common/ci/i18n";
import { AuthField, AuthHeading, AuthShell, EyeToggle } from "../AuthShell";

/** Password bhool gaye — step 3: naya password. */
const ForgotPassword: React.FC = () => {
  const t = useCiT();
  const navigate = useNavigate();
  const location = useLocation();
  const { phoneNumber, otp } = (location?.state || {}) as {
    phoneNumber?: string;
    otp?: string;
  };

  const [mutation, { data, isLoading, isSuccess, isError, error }] =
    useForgetPasswordMutation();
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<{ pw?: string; cpw?: string }>({});

  useEffect(() => {
    if (!phoneNumber || !otp) navigate("/forgot-password/request-otp", { replace: true });
  }, [phoneNumber, otp, navigate]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const next: { pw?: string; cpw?: string } = {};
    if (!newPassword) next.pw = t("Naya password daalein");
    else if (newPassword.length < 6) next.pw = t("Password kam se kam 6 akshar ka ho");
    if (confirmPassword !== newPassword) next.cpw = t("Dono password alag hain");
    setErrors(next);
    if (Object.keys(next).length) return;
    mutation({ phoneNumber: Number(phoneNumber), otp, password: newPassword });
  };

  useEffect(() => {
    if (isSuccess) {
      toast.success(data?.message || t("Password badal gaya"));
      localStorage.removeItem("otpTimerStart2");
      navigate("/signup");
    } else if (isError) {
      toast.error(error?.data?.message || t("Kuch galat ho gaya"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, isError]);

  if (!phoneNumber || !otp) return null;

  return (
    <AuthShell standalone>
      <form onSubmit={handleSubmit} noValidate>
        <AuthHeading
          title={t("Naya password")}
          sub={t("Naya password rakhein — iske baad usi se sign in karein.")}
        />

        <div className="flex flex-col gap-4">
          <AuthField
            label={t("Naya password")}
            type={showNewPassword ? "text" : "password"}
            autoComplete="new-password"
            placeholder={t("Kam se kam 6 akshar")}
            icon={<LuLock size={18} />}
            value={newPassword}
            onChange={(e) => {
              setNewPassword(e.target.value);
              setErrors((p) => ({ ...p, pw: "" }));
            }}
            error={errors.pw}
            right={
              <EyeToggle
                shown={showNewPassword}
                onToggle={() => setShowNewPassword((p) => !p)}
              />
            }
          />

          <AuthField
            label={t("Password dobara")}
            type={showConfirmPassword ? "text" : "password"}
            autoComplete="new-password"
            placeholder={t("Wahi password dobara")}
            icon={<LuLock size={18} />}
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setErrors((p) => ({ ...p, cpw: "" }));
            }}
            error={errors.cpw}
            right={
              <EyeToggle
                shown={showConfirmPassword}
                onToggle={() => setShowConfirmPassword((p) => !p)}
              />
            }
          />

          <button
            type="submit"
            disabled={isLoading}
            className="inline-flex h-[52px] w-full items-center justify-center gap-2 rounded-md border border-ci-orange bg-ci-orange text-[17px] font-bold uppercase tracking-wide text-white transition hover:border-ci-orange2 hover:bg-ci-orange2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isLoading ? (
              <>
                <FaSpinner className="animate-spin" />
                {t("Badal rahe hain…")}
              </>
            ) : (
              t("Password badlein")
            )}
          </button>

          <p className="text-center text-[14px] text-ci-muted">
            <Link to="/signup" className="font-semibold text-ci-navy underline underline-offset-2">
              {t("Sign in karein")}
            </Link>
          </p>
        </div>
      </form>
    </AuthShell>
  );
};

export default ForgotPassword;
